import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  UserPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Modal,
  Pagination,
  Radio,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  rem,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useState } from "react";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { useCurrentUser } from "../../hooks/queries/useCurrentUser";
import {
  User,
  getDisplayName,
  getUserRole,
  hasNoCv,
} from "../../models/user.model";
import { AdministrationService } from "../../services/administrationService";

const administrationService = new AdministrationService();

export function OperationsUsers() {
  const [admins, setAdmins] = useState<User[]>([]);
  const [managers, setManagers] = useState<User[]>([]);
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [createOpened, { open: openCreate, close: closeCreate }] =
    useDisclosure(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<string>("20");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState<"admin" | "manager">(
    "manager"
  );

  // Load data on component mount
  useEffect(() => {
    if (currentUser && !userLoading) {
      loadData();
    }
  }, [currentUser, userLoading]);

  const loadData = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);

      // Load appropriate data based on user permissions
      if (currentUser.isAdmin) {
        // Admins can see both admins and managers
        const [adminsList, managersList] = await Promise.all([
          administrationService.getAdmins(),
          administrationService.getManagers(),
        ]);
        setAdmins(adminsList);
        setManagers(managersList);
      } else if (currentUser.isManager) {
        // Managers can only see managers
        const managersList = await administrationService.getManagers();
        setManagers(managersList);
        setAdmins([]); // Clear admins for managers
      }
    } catch (error) {
      console.error("Error loading data:", error);
      notifications.show({
        title: "Error",
        message: "Failed to load users data",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  // Combine and filter users based on search query and permissions
  const allUsers = useMemo(() => {
    const combinedUsers: User[] = [];

    if (currentUser?.isAdmin) {
      // Admins see both admins and managers
      combinedUsers.push(...admins, ...managers);
    } else if (currentUser?.isManager) {
      // Managers see only managers
      combinedUsers.push(...managers);
    }

    return combinedUsers;
  }, [admins, managers, currentUser]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return allUsers;
    }

    const lowercaseQuery = searchQuery.toLowerCase().trim();
    return allUsers.filter(
      (user) =>
        user.email.toLowerCase().includes(lowercaseQuery) ||
        (user.firstName &&
          user.firstName.toLowerCase().includes(lowercaseQuery)) ||
        (user.lastName &&
          user.lastName.toLowerCase().includes(lowercaseQuery)) ||
        getDisplayName(user).toLowerCase().includes(lowercaseQuery)
    );
  }, [allUsers, searchQuery]);

  // Get paginated users
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * parseInt(itemsPerPage);
    const endIndex = startIndex + parseInt(itemsPerPage);
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = useMemo(
    () => Math.ceil(filteredUsers.length / parseInt(itemsPerPage)),
    [filteredUsers, itemsPerPage]
  );

  // Reset to first page when search query or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, itemsPerPage]);

  // Handle opening the delete confirmation modal
  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    open();
  };

  // Handle confirming delete
  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    try {
      setDeleting(true);
      await administrationService.deleteUser(selectedUser.email);

      notifications.show({
        title: "Success",
        message: `User ${getDisplayName(selectedUser)} has been deleted`,
        color: "green",
      });

      // Reload data after successful deletion
      await loadData();
      close();
    } catch (error) {
      console.error("Error deleting user:", error);
      notifications.show({
        title: "Error",
        message: "Failed to delete user",
        color: "red",
      });
    } finally {
      setDeleting(false);
    }
  };

  // Handle opening create user modal
  const handleCreateUser = () => {
    setNewUserEmail("");
    setNewUserRole("manager");
    openCreate();
  };

  // Handle creating user
  const handleConfirmCreate = async () => {
    if (!newUserEmail.trim()) {
      notifications.show({
        title: "Error",
        message: "Please enter a valid email address",
        color: "red",
      });
      return;
    }

    try {
      setCreating(true);

      if (newUserRole === "admin") {
        await administrationService.createAdmin(newUserEmail.trim());
      } else {
        await administrationService.createManager(newUserEmail.trim());
      }

      notifications.show({
        title: "Success",
        message: `${newUserRole === "admin" ? "Admin" : "Manager"
          } user has been created successfully`,
        color: "green",
      });

      // Reload data after successful creation
      await loadData();
      closeCreate();
    } catch (error) {
      console.error("Error creating user:", error);
      notifications.show({
        title: "Error",
        message: "Failed to create user",
        color: "red",
      });
    } finally {
      setCreating(false);
    }
  };

  // Get role badge
  const getRoleBadge = (user: User) => {
    const role = getUserRole(user);
    const color =
      role === "Admin" ? "red" : role === "Manager" ? "blue" : "gray";
    return (
      <Badge color={color} variant="light">
        {role}
      </Badge>
    );
  };

  // Get status badge
  const getStatusBadge = (user: User) => {
    if (hasNoCv(user)) {
      return (
        <Badge color="orange" variant="light">
          NO CV
        </Badge>
      );
    }
    if (user.isBlocked) {
      return (
        <Badge color="red" variant="light">
          Blocked
        </Badge>
      );
    }
    return (
      <Badge color="green" variant="light">
        Active
      </Badge>
    );
  };

  if (loading) {
    return (
      <Container size="xl" p="md">
        <Center h={200}>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (!currentUser) {
    return (
      <Container size="xl" p="md">
        <Alert
          icon={
            <ExclamationTriangleIcon
              style={{ width: rem(16), height: rem(16) }}
            />
          }
          title="Access Denied"
          color="red"
        >
          You don't have permission to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" p="md">
      <Breadcrumb
        items={[
          { label: "Operations Panel", href: "/operations" },
          { label: "Users Management", isActive: true },
        ]}
      />
      <Group justify="space-between" mb="xl">
        <Title order={1} size="h2" fw={700}>
          Users Management
        </Title>
        {currentUser?.isAdmin && (
          <Button
            leftSection={
              <UserPlusIcon style={{ width: rem(16), height: rem(16) }} />
            }
            onClick={handleCreateUser}
            variant="filled"
          >
            Add User
          </Button>
        )}
      </Group>

      {/* Info about permissions */}
      <Alert mb="lg" color="blue">
        {currentUser.isAdmin
          ? "As an admin, you can view and manage both administrators and managers."
          : "As a manager, you can view and manage other managers."}
      </Alert>

      {/* Search bar */}
      <Box mb="lg">
        <TextInput
          placeholder="Search users by name or email..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          leftSection={
            <MagnifyingGlassIcon style={{ width: rem(18), height: rem(18) }} />
          }
          rightSection={
            searchQuery ? (
              <ActionIcon
                onClick={() => setSearchQuery("")}
                variant="subtle"
                size="sm"
              >
                <XMarkIcon style={{ width: rem(16), height: rem(16) }} />
              </ActionIcon>
            ) : null
          }
          size="md"
        />
      </Box>

      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {paginatedUsers.length > 0 ? (
            paginatedUsers.map((user) => (
              <Table.Tr
                key={user.email}
                opacity={hasNoCv(user) ? 0.7 : 1}
              >
                <Table.Td>
                  <Text fw={hasNoCv(user) ? 400 : 500}>
                    {getDisplayName(user)}
                  </Text>
                  {hasNoCv(user) && (
                    <Text size="xs" c="dimmed">
                      No profile data
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{user.email}</Text>
                </Table.Td>
                <Table.Td>{getRoleBadge(user)}</Table.Td>
                <Table.Td>{getStatusBadge(user)}</Table.Td>
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => handleDeleteUser(user)}
                    title="Delete user"
                  >
                    <TrashIcon style={{ width: rem(16), height: rem(16) }} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))
          ) : (
            <Table.Tr>
              <Table.Td colSpan={5} align="center">
                <Text c="dimmed" py="md">
                  No users found matching your search criteria
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      {/* Pagination */}
      {filteredUsers.length > 0 && (
        <Group mt="md" justify="space-between">
          <Group>
            <Text size="sm">Rows per page:</Text>
            <Select
              value={itemsPerPage}
              onChange={(value) => setItemsPerPage(value || "20")}
              data={[
                { value: "10", label: "10" },
                { value: "20", label: "20" },
                { value: "50", label: "50" },
              ]}
              size="xs"
              style={{ width: 70 }}
            />
          </Group>
          <Pagination
            total={totalPages}
            value={currentPage}
            onChange={setCurrentPage}
            size="sm"
          />
        </Group>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        opened={opened}
        onClose={close}
        title={
          <Text fw={700} size="lg">
            Delete User
          </Text>
        }
        size="md"
      >
        <Stack>
          <Alert
            icon={
              <ExclamationTriangleIcon
                style={{ width: rem(16), height: rem(16) }}
              />
            }
            color="red"
          >
            Are you sure you want to delete this user?
          </Alert>

          {selectedUser && (
            <Box
              p="md"
              style={{
                backgroundColor: "var(--mantine-color-gray-0)",
                borderRadius: "var(--mantine-radius-md)",
              }}
            >
              <Text fw={500}>{getDisplayName(selectedUser)}</Text>
              <Text size="sm" c="dimmed">
                {selectedUser.email}
              </Text>
              {hasNoCv(selectedUser) && (
                <Badge color="orange" variant="light" mt="xs">
                  NO CV
                </Badge>
              )}
            </Box>
          )}

          <Text size="sm" c="dimmed">
            This action cannot be undone. The user will be permanently removed
            from the system.
          </Text>

          <Group justify="right" mt="xl">
            <Button variant="subtle" onClick={close} disabled={deleting}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleConfirmDelete}
              loading={deleting}
            >
              Delete User
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create User Modal */}
      <Modal
        opened={createOpened}
        onClose={closeCreate}
        title={
          <Text fw={700} size="lg">
            Add New User
          </Text>
        }
        size="md"
      >
        <Stack>
          <TextInput
            label="Email Address"
            placeholder="Enter user email address"
            value={newUserEmail}
            onChange={(event) => setNewUserEmail(event.currentTarget.value)}
            required
            type="email"
          />

          <Radio.Group
            value={newUserRole}
            onChange={(value) => setNewUserRole(value as "admin" | "manager")}
            label="User Role"
            required
          >
            <Stack mt="xs">
              <Radio value="manager" label="Manager" />
              <Radio value="admin" label="Administrator" />
            </Stack>
          </Radio.Group>

          <Alert color="blue" mt="md">
            <Text size="sm">
              The user will be created with the specified role. They will need
              to sign in to access the system.
            </Text>
          </Alert>

          <Group justify="right" mt="xl">
            <Button variant="subtle" onClick={closeCreate} disabled={creating}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmCreate}
              loading={creating}
              disabled={!newUserEmail.trim()}
            >
              Create User
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
